import { LabelResolver } from "@ax/office-resources-common";
import { $format } from "@ax/office-shared-lib";

// tslint:disable:max-line-length
/** The mechanism used to resolve labels related to the "AppletInteraction" concept. */
export class AppletInteractionLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Applet or resource "{0}" has an invalid relative URL: {1}". The error shown when an applet or resource's relative URL is invalid..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_InvalidRelativeUrlFormat" label.
     */
    public invalidRelativeUrlFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_InvalidRelativeUrlFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "Unable to load applet with ID "{0}"". The unable to load applet error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_UnableToLoadAppletErrorFormat" label.
     */
    public unableToLoadAppletErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_UnableToLoadAppletErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Unable to update the options for the applet with ID "{0}".  The applet may not function properly until the app is reloaded.". The unable to update applet options error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_UnableToUpdateAppletOptionsErrorFormat" label.
     */
    public unableToUpdateAppletOptionsErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_UnableToUpdateAppletOptionsErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Timed out while trying to configure applet with ID "{0}"". The applet configuration timed out error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletConfigurationTimedOutErrorFormat" label.
     */
    public appletConfigurationTimedOutErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletConfigurationTimedOutErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "An error occurred while trying to localize applet with ID "{0}"". The error shown when applet labels fail to be localized..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletLocalizationErrorFormat" label.
     */
    public appletLocalizationErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletLocalizationErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Unable to load resource with ID "{0}"". The unable to load resource error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_UnableToLoadResourceErrorFormat" label.
     */
    public unableToLoadResourceErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_UnableToLoadResourceErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Timed out while trying to configure resource with ID "{0}"". The resource configuration timed out error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_ResourceConfigurationTimedOutErrorFormat" label.
     */
    public resourceConfigurationTimedOutErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_ResourceConfigurationTimedOutErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Resource "{0}" is not registered". The resource not registered error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_ResourceNotRegisteredErrorFormat" label.
     */
    public resourceNotRegisteredErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_ResourceNotRegisteredErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Continue?". The applets loaded changes user confirmation message label..
     * @returns The localized resource corresponding to the "AppletInteraction_UserConfirmAppletsLoadedChangesMessage" label.
     */
    public userConfirmAppletsLoadedChangesMessage(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_UserConfirmAppletsLoadedChangesMessage");
    }

    /**
     * Translates a resource similar to "The following applets will be loaded:\n{0}\n\n{1}". The applets to be loaded message format label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletsToBeLoadedMessageFormat" label.
     */
    public appletsToBeLoadedMessageFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletsToBeLoadedMessageFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "The following applets will be unloaded:\n{0}\n\n{1}". The applets to be unloaded message format label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletsToBeUnloadedMessageFormat" label.
     */
    public appletsToBeUnloadedMessageFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletsToBeUnloadedMessageFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to ", ". The applets list separator label..
     * @returns The localized resource corresponding to the "AppletInteraction_AppletListSeparator" label.
     */
    public appletListSeparator(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_AppletListSeparator");
    }

    /**
     * Translates a resource similar to "Loading {0} applet". The applet loading message label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletLoadingMessageFormat" label.
     */
    public appletLoadingMessageFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletLoadingMessageFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Loading {0} applet failed". The applet loading failed label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletLoadingFailedMessageFormat" label.
     */
    public appletLoadingFailedMessageFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletLoadingFailedMessageFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "{0} applet loaded". The applet loading succeeded message label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletLoadingSucceededMessageFormat" label.
     */
    public appletLoadingSucceededMessageFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletLoadingSucceededMessageFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Unable to load applet with ID "{0}" as it isn't registered". The applet to load isn't registered label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AppletToLoadNotRegisteredMessageFormat" label.
     */
    public appletToLoadNotRegisteredMessageFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AppletToLoadNotRegisteredMessageFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "No applet registrations found". The no applet registrations found label..
     * @returns The localized resource corresponding to the "AppletInteraction_NoAppletRegistrationsFoundMessageFormat" label.
     */
    public noAppletRegistrationsFoundMessageFormat(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_NoAppletRegistrationsFoundMessageFormat");
    }

    /**
     * Translates a resource similar to "Authentication error". The authentication error label..
     * @returns The localized resource corresponding to the "AppletInteraction_AuthenticationError" label.
     */
    public authenticationError(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_AuthenticationError");
    }

    /**
     * Translates a resource similar to "The session has expired.". The authentication expired error label..
     * @returns The localized resource corresponding to the "AppletInteraction_AuthenticationExpiredError" label.
     */
    public authenticationExpiredError(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_AuthenticationExpiredError");
    }

    /**
     * Translates a resource similar to "Unable to sign in as {0} ({1}). Please see the message center for details.". The authentication error details format..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AuthenticationDetailsFormat" label.
     */
    public authenticationDetailsFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AuthenticationDetailsFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "Retrieving common applet configuration failed.". The common applet configuration retrieval failed error label..
     * @returns The localized resource corresponding to the "AppletInteraction_CommonAppletConfigRetrievalFailed" label.
     */
    public commonAppletConfigRetrievalFailed(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_CommonAppletConfigRetrievalFailed");
    }

    /**
     * Translates a resource similar to "Data Connector". Label for the name of the Data Connector applet..
     * @returns The localized resource corresponding to the "AppletInteraction_DataConnectorName" label.
     */
    public dataConnectorName(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_DataConnectorName");
    }

    /**
     * Translates a resource similar to "Connects to OData services to facilitate reading data, editing data, publishing data changes, and workbook template design.". Label for the description of the Excel Data Connector applet..
     * @returns The localized resource corresponding to the "AppletInteraction_DataConnectorDescription" label.
     */
    public dataConnectorDescription(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_DataConnectorDescription");
    }

    /**
     * Translates a resource similar to "Template Designer". Label for the name of the Word Template Designer applet..
     * @returns The localized resource corresponding to the "AppletInteraction_TemplateDesignerName" label.
     */
    public templateDesignerName(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_TemplateDesignerName");
    }

    /**
     * Translates a resource similar to "Connects to OData services to facilitate document template design.". Label for the description of the Word Template Designer applet..
     * @returns The localized resource corresponding to the "AppletInteraction_TemplateDesignerDescription" label.
     */
    public templateDesignerDescription(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_TemplateDesignerDescription");
    }

    /**
     * Translates a resource similar to "Request reconnection address is the same as the current connection address; no reconnection will occur.". The notification message displayed when the a requested reconnection address doesn't differ from the add-in's current connection address..
     * @returns The localized resource corresponding to the "AppletInteraction_ConnectionNotChangedMessage" label.
     */
    public connectionNotChangedMessage(): string {
        return this.labelResolver.resolveLabel("AppletInteraction_ConnectionNotChangedMessage");
    }

    /**
     * Translates a resource similar to "You are about to change the connection which will cause the add-in to reload and may require reauthentication.\n\nContinue?\n\nOld Connection:\n{0}\n\nNew Connection:\n{1}". The confirmation message displayed when the add-in is requested to reconnect..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "AppletInteraction_AddinReconnectMessage" label.
     */
    public addinReconnectMessage(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("AppletInteraction_AddinReconnectMessage"), formatParam1, formatParam2);
    }
}

/** The mechanism used to resolve labels related to the "OfficeInteraction" concept. */
export class OfficeInteractionLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Subscription to document event "{0}" failed.". The applet document subscription error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_AppletDocumentSubscriptionErrorFormat" label.
     */
    public appletDocumentSubscriptionErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_AppletDocumentSubscriptionErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Table binding retrieval failed.". The applet table binding retrieval error..
     * @returns The localized resource corresponding to the "OfficeInteraction_AppletTableBindingRetrievalError" label.
     */
    public appletTableBindingRetrievalError(): string {
        return this.labelResolver.resolveLabel("OfficeInteraction_AppletTableBindingRetrievalError");
    }

    /**
     * Translates a resource similar to "Subscription to table binding event "{0}" failed.". The applet table binding subscription error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_AppletTableBindingSubscriptionErrorFormat" label.
     */
    public appletTableBindingSubscriptionErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_AppletTableBindingSubscriptionErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Execution of table binding method "{0}" failed.". The applet table binding method error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_AppletTableBindingMethodErrorFormat" label.
     */
    public appletTableBindingMethodErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_AppletTableBindingMethodErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Executing document method "{0}" failed.". The executing document method error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_ExecutingDocumentMethodErrorFormat" label.
     */
    public executingDocumentMethodErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_ExecutingDocumentMethodErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Executing document bindings method "{0}" failed.". The executing document bindings method error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_ExecutingBindingsMethodErrorFormat" label.
     */
    public executingBindingsMethodErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_ExecutingBindingsMethodErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Unable to find method with name "{0}".". The Office method not found error label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "OfficeInteraction_OfficeMethodNotFoundErrorFormat" label.
     */
    public officeMethodNotFoundErrorFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("OfficeInteraction_OfficeMethodNotFoundErrorFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Office 2016 or later is required to run this app.". The error message displayed when Office 2016 APIs are used, but the Office application hosting the app is using a prior version of Office..
     * @returns The localized resource corresponding to the "OfficeInteraction_Office2016RequiredError" label.
     */
    public office2016RequiredError(): string {
        return this.labelResolver.resolveLabel("OfficeInteraction_Office2016RequiredError");
    }
}

/** The mechanism used to resolve labels related to the "Authentication" concept. */
export class AuthenticationLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Unable to load authentication resource". The unable to load authentication resource error label..
     * @returns The localized resource corresponding to the "Authentication_UnableToLoadAuthenticationResourceError" label.
     */
    public unableToLoadAuthenticationResourceError(): string {
        return this.labelResolver.resolveLabel("Authentication_UnableToLoadAuthenticationResourceError");
    }

    /**
     * Translates a resource similar to "Invalid server configuration". The invalid server configuration error label..
     * @returns The localized resource corresponding to the "Authentication_InvalidServerConfiguration" label.
     */
    public invalidServerConfiguration(): string {
        return this.labelResolver.resolveLabel("Authentication_InvalidServerConfiguration");
    }

    /**
     * Translates a resource similar to "No authentication realm is defined". The no authentication realm was defined error label..
     * @returns The localized resource corresponding to the "Authentication_NoAuthRealm" label.
     */
    public noAuthRealm(): string {
        return this.labelResolver.resolveLabel("Authentication_NoAuthRealm");
    }

    /**
     * Translates a resource similar to "No authentication client id is defined". The no authentication client id was defined error label..
     * @returns The localized resource corresponding to the "Authentication_NoAuthClientId" label.
     */
    public noAuthClientId(): string {
        return this.labelResolver.resolveLabel("Authentication_NoAuthClientId");
    }

    /**
     * Translates a resource similar to "No authentication resource id is defined". The no authentication resource id was defined error label..
     * @returns The localized resource corresponding to the "Authentication_NoAuthResourceId" label.
     */
    public noAuthResourceId(): string {
        return this.labelResolver.resolveLabel("Authentication_NoAuthResourceId");
    }

    /**
     * Translates a resource similar to "Authentication context failed to load". The error shown when the ADAL JS library fails to load..
     * @returns The localized resource corresponding to the "Authentication_NoAuthenticationContext" label.
     */
    public noAuthenticationContext(): string {
        return this.labelResolver.resolveLabel("Authentication_NoAuthenticationContext");
    }

    /**
     * Translates a resource similar to "An error was encountered while attempting to authenticate". The error shown when the auth dialog encounters an error..
     * @returns The localized resource corresponding to the "Authentication_AuthDialogError" label.
     */
    public authDialogError(): string {
        return this.labelResolver.resolveLabel("Authentication_AuthDialogError");
    }

    /**
     * Translates a resource similar to "Logged in as {0}". The logged in as label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Authentication_LoggedInAs" label.
     */
    public loggedInAs(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Authentication_LoggedInAs"), formatParam1);
    }

    /**
     * Translates a resource similar to "This add-in enables viewing and editing of your data.\n\nPlease sign in with your Common Data Service user account.". The message format used on the sign in page..
     * @returns The localized resource corresponding to the "Authentication_SignInAsFormat" label.
     */
    public signInAsFormat(): string {
        return this.labelResolver.resolveLabel("Authentication_SignInAsFormat");
    }

    /**
     * Translates a resource similar to "A sign-in was attempted, but the authentication cookie may not be permanent or may be in a bad state. Try signing in again, ensuring that "Keep me signed in" is checked. If the problem persists, consider clearing the Internet Explorer browser cookie cache, closing all Internet Explorer and Office windows, and signing in with "Keep me signed in" checked.". The message displayed to the user when manual sign in has been attempted and has failed..
     * @returns The localized resource corresponding to the "Authentication_ManualSignInAttempted" label.
     */
    public manualSignInAttempted(): string {
        return this.labelResolver.resolveLabel("Authentication_ManualSignInAttempted");
    }

    /**
     * Translates a resource similar to "Sign out". The label for the sign out link..
     * @returns The localized resource corresponding to the "Authentication_SignOutLabel" label.
     */
    public signOutLabel(): string {
        return this.labelResolver.resolveLabel("Authentication_SignOutLabel");
    }

    /**
     * Translates a resource similar to "Signing out". The message displayed when signing out..
     * @returns The localized resource corresponding to the "Authentication_SigningOutMessage" label.
     */
    public signingOutMessage(): string {
        return this.labelResolver.resolveLabel("Authentication_SigningOutMessage");
    }

    /**
     * Translates a resource similar to "Sign out successful". The message displayed when sign out completes successfully..
     * @returns The localized resource corresponding to the "Authentication_SignedOutMessage" label.
     */
    public signedOutMessage(): string {
        return this.labelResolver.resolveLabel("Authentication_SignedOutMessage");
    }

    /**
     * Translates a resource similar to "Sign out failed". The message displayed when sign out fails..
     * @returns The localized resource corresponding to the "Authentication_SignOutFailedMessage" label.
     */
    public signOutFailedMessage(): string {
        return this.labelResolver.resolveLabel("Authentication_SignOutFailedMessage");
    }

    /**
     * Translates a resource similar to "Unable to open authentication window". The error message displayed when opening a window to facilitate online authentication fails..
     * @returns The localized resource corresponding to the "Authentication_OnlineWindowNotOpenedError" label.
     */
    public onlineWindowNotOpenedError(): string {
        return this.labelResolver.resolveLabel("Authentication_OnlineWindowNotOpenedError");
    }
}

/** The mechanism used to resolve labels related to the "DynamicsApp" concept. */
export class DynamicsAppLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "The version of Internet Explorer being used is not supported. Please update Internet Explorer to a newer version.". The error shown when the browser is an unsupported version of Internet Explorer..
     * @returns The localized resource corresponding to the "DynamicsApp_UnsupportedIE" label.
     */
    public unsupportedie(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnsupportedIE");
    }

    /**
     * Translates a resource similar to "The web browser running the add-in is not allowing access to local or session storage. Please adjust the environment and consult the support pages if neccessary.". The error shown when the browser doesn't support local or session storage..
     * @returns The localized resource corresponding to the "DynamicsApp_UnsupportedStorage" label.
     */
    public unsupportedStorage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnsupportedStorage");
    }

    /**
     * Translates a resource similar to "The currently running application is not recognized by the Microsoft app.". The error shown when the running application isn't recognized..
     * @returns The localized resource corresponding to the "DynamicsApp_UnrecognizedApplicationError" label.
     */
    public unrecognizedApplicationError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnrecognizedApplicationError");
    }

    /**
     * Translates a resource similar to "An error occurred while retrieving applet and resource registration information. Please contact your system administrator.". The error shown when applet or resource registrations could not be retrieved..
     * @returns The localized resource corresponding to the "DynamicsApp_RegistrationRetrievalError" label.
     */
    public registrationRetrievalError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RegistrationRetrievalError");
    }

    /**
     * Translates a resource similar to "Verifying authentication". The verifying authentication label..
     * @returns The localized resource corresponding to the "DynamicsApp_Authentication_Verifying" label.
     */
    public authentication_Verifying(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Authentication_Verifying");
    }

    /**
     * Translates a resource similar to "Not authenticated". The not authenticated label..
     * @returns The localized resource corresponding to the "DynamicsApp_Authentication_NotAuthenticated" label.
     */
    public authentication_NotAuthenticated(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Authentication_NotAuthenticated");
    }

    /**
     * Translates a resource similar to "Authenticated". The authenticated label..
     * @returns The localized resource corresponding to the "DynamicsApp_Authentication_Authenticated" label.
     */
    public authentication_Authenticated(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Authentication_Authenticated");
    }

    /**
     * Translates a resource similar to "You do not have permission to interact with this system.". The error shown when a user is determined to be unauthorized to interact with a system..
     * @returns The localized resource corresponding to the "DynamicsApp_NotAuthorizedError" label.
     */
    public notAuthorizedError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NotAuthorizedError");
    }

    /**
     * Translates a resource similar to "{0}\n\nUser: {1} ({2})\nServer: {3}". The splash screen description shown when a user is determined to be unauthorized to interact with a system where {0} is the not authorized error, {1} is the user's display name, {2} is the user's user name, and {3} is the current server for which the user isn't authorized..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @param formatParam3 Format parameter number 3 for the label.
     * @param formatParam4 Format parameter number 4 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_NotAuthorizedDescriptionFormat" label.
     */
    public notAuthorizedDescriptionFormat(formatParam1: any, formatParam2: any, formatParam3: any, formatParam4: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_NotAuthorizedDescriptionFormat"), formatParam1, formatParam2, formatParam3, formatParam4);
    }

    /**
     * Translates a resource similar to "Sign in". The sign in button label..
     * @returns The localized resource corresponding to the "DynamicsApp_SignInButtonLabel" label.
     */
    public signInButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_SignInButtonLabel");
    }

    /**
     * Translates a resource similar to "Opens the sign in page". The sign in button title..
     * @returns The localized resource corresponding to the "DynamicsApp_SignInButtonTitle" label.
     */
    public signInButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_SignInButtonTitle");
    }

    /**
     * Translates a resource similar to "Copilot for Finance (preview) is available". Title for Copilot for Finance banner..
     * @returns The localized resource corresponding to the "DynamicsApp_CopilotForFinanceBannerTitle" label.
     */
    public copilotForFinanceBannerTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_CopilotForFinanceBannerTitle");
    }

    /**
     * Translates a resource similar to "Experience your finance assistant working right alongside you in Microsoft 365 applications to stay in the flow of work connecting productivity tools with existing financial systems.". Main text to be displayed within the Copilot for Finance banner..
     * @returns The localized resource corresponding to the "DynamicsApp_CopilotForFinanceBannerBodyText" label.
     */
    public copilotForFinanceBannerBodyText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_CopilotForFinanceBannerBodyText");
    }

    /**
     * Translates a resource similar to "Learn more". Text for link in Copilot for Finance banner..
     * @returns The localized resource corresponding to the "DynamicsApp_CopilotForFinanceBannerLinkText" label.
     */
    public copilotForFinanceBannerLinkText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_CopilotForFinanceBannerLinkText");
    }

    /**
     * Translates a resource similar to "External links". The external links heading label..
     * @returns The localized resource corresponding to the "DynamicsApp_ExternalLinksHeading" label.
     */
    public externalLinksHeading(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_ExternalLinksHeading");
    }

    /**
     * Translates a resource similar to "Add-in Details". The add-in details link label..
     * @returns The localized resource corresponding to the "DynamicsApp_AddinDetailsLinkLabel" label.
     */
    public addinDetailsLinkLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AddinDetailsLinkLabel");
    }

    /**
     * Translates a resource similar to "Dynamics LCS". The Dynamics LCS link label..
     * @returns The localized resource corresponding to the "DynamicsApp_lcsLinkLabel" label.
     */
    public lcsLinkLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_lcsLinkLabel");
    }

    /**
     * Translates a resource similar to "Settings could not be saved". The unable to save settings error label..
     * @returns The localized resource corresponding to the "DynamicsApp_UnableToSaveSettingsError" label.
     */
    public unableToSaveSettingsError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnableToSaveSettingsError");
    }

    /**
     * Translates a resource similar to "Unsaved settings will be used until this file is closed.". The unable to save setting detail label..
     * @returns The localized resource corresponding to the "DynamicsApp_UnableToSaveSettingsDetail" label.
     */
    public unableToSaveSettingsDetail(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnableToSaveSettingsDetail");
    }

    /**
     * Translates a resource similar to "{0}...". The label used when displaying a busy status..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_Status_BusyFormattedMessage" label.
     */
    public status_BusyFormattedMessage(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_Status_BusyFormattedMessage"), formatParam1);
    }

    /**
     * Translates a resource similar to "Confirm Change". The options changes confirmation title..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsConfirmChangesTitle" label.
     */
    public optionsConfirmChangesTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsConfirmChangesTitle");
    }

    /**
     * Translates a resource similar to "You have modified a setting that requires the app to be reloaded.\n\nContinue?". The connection changes require app reload confirmation message..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsConfirmAppReloadMessage" label.
     */
    public optionsConfirmAppReloadMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsConfirmAppReloadMessage");
    }

    /**
     * Translates a resource similar to "Options". The title for the options dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsDialogTitle" label.
     */
    public optionsDialogTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsDialogTitle");
    }

    /**
     * Translates a resource similar to "User menu". The label used for the user button tooltip..
     * @returns The localized resource corresponding to the "DynamicsApp_UserButtonTooltip" label.
     */
    public userButtonTooltip(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UserButtonTooltip");
    }

    /**
     * Translates a resource similar to "App". The title for the App options..
     * @returns The localized resource corresponding to the "DynamicsApp_DynamicsAppOptionsTitle" label.
     */
    public dynamicsAppOptionsTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_DynamicsAppOptionsTitle");
    }

    /**
     * Translates a resource similar to "General". The title for the App general options group..
     * @returns The localized resource corresponding to the "DynamicsApp_DynamicsAppGeneralOptionsTitle" label.
     */
    public dynamicsAppGeneralOptionsTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_DynamicsAppGeneralOptionsTitle");
    }

    /**
     * Translates a resource similar to "Environment: {0}". The text for the Environment element..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_DynamicsAppEnvironmentText" label.
     */
    public dynamicsAppEnvironmentText(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_DynamicsAppEnvironmentText"), formatParam1);
    }

    /**
     * Translates a resource similar to "Company: {0}". The text for the Company element..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_DynamicsAppCompanyText" label.
     */
    public dynamicsAppCompanyText(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_DynamicsAppCompanyText"), formatParam1);
    }

    /**
     * Translates a resource similar to "Cancel". The text for the options dialog cancel button..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsCancelButtonText" label.
     */
    public optionsCancelButtonText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsCancelButtonText");
    }

    /**
     * Translates a resource similar to "Closes the options dialog without saving". The title for the options dialog cancel button..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsCancelButtonTitle" label.
     */
    public optionsCancelButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsCancelButtonTitle");
    }

    /**
     * Translates a resource similar to "OK". The text for the options dialog OK button..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsOkButtonText" label.
     */
    public optionsOkButtonText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsOkButtonText");
    }

    /**
     * Translates a resource similar to "Accepts the changes and closes the options dialog". The title for the options dialog OK button..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsOkButtonTitle" label.
     */
    public optionsOkButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsOkButtonTitle");
    }

    /**
     * Translates a resource similar to "Connection". The text for the label of the server input in the options dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsDialogServerText" label.
     */
    public optionsDialogServerText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsDialogServerText");
    }

    /**
     * Translates a resource similar to "Language". The text for the label of the language input in the options dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsDialogLanguageText" label.
     */
    public optionsDialogLanguageText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsDialogLanguageText");
    }

    /**
     * Translates a resource similar to "Telemetry". The title for the telemetry options group..
     * @returns The localized resource corresponding to the "DynamicsApp_TelemetryOptionsTitle" label.
     */
    public telemetryOptionsTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_TelemetryOptionsTitle");
    }

    /**
     * Translates a resource similar to "App version". The text for the label of the app version input in the options dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsDialogVersionText" label.
     */
    public optionsDialogVersionText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsDialogVersionText");
    }

    /**
     * Translates a resource similar to "App correlation ID". The text for the label of the correlation ID input in the options dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsDialogCorrelationIdText" label.
     */
    public optionsDialogCorrelationIdText(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsDialogCorrelationIdText");
    }

    /**
     * Translates a resource similar to "Options". The options button label..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsButtonLabel" label.
     */
    public optionsButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsButtonLabel");
    }

    /**
     * Translates a resource similar to "Open options dialog". The options button tooltip..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsButtonTooltip" label.
     */
    public optionsButtonTooltip(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsButtonTooltip");
    }

    /**
     * Translates a resource similar to "Advanced". The advanced options group label..
     * @returns The localized resource corresponding to the "DynamicsApp_DynamicsAppAdvancedOptionsTitle" label.
     */
    public dynamicsAppAdvancedOptionsTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_DynamicsAppAdvancedOptionsTitle");
    }

    /**
     * Translates a resource similar to "Configure applets". The applet config button label..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsAppletConfigButtonLabel" label.
     */
    public optionsAppletConfigButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsAppletConfigButtonLabel");
    }

    /**
     * Translates a resource similar to "Opens applets configuration dialog". The applet config button title..
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsAppletConfigButtonTitle" label.
     */
    public optionsAppletConfigButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OptionsAppletConfigButtonTitle");
    }

    /**
     * Translates a resource similar to "Performs the "{0}" action". The format used for applet button titles..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_OptionsAppletButtonTitleFormat" label.
     */
    public optionsAppletButtonTitleFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_OptionsAppletButtonTitleFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Configuration". The applet config dialog title label..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigDialogTitle" label.
     */
    public appletConfigDialogTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigDialogTitle");
    }

    /**
     * Translates a resource similar to "Enable Applets". The applet config group title label..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigGroupTitle" label.
     */
    public appletConfigGroupTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigGroupTitle");
    }

    /**
     * Translates a resource similar to "OK". The applet config dialog OK button label..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigOkButtonLabel" label.
     */
    public appletConfigOkButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigOkButtonLabel");
    }

    /**
     * Translates a resource similar to "Accepts the changes and closes the applet configuration dialog". The applet config dialog OK button title..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigOkButtonTitle" label.
     */
    public appletConfigOkButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigOkButtonTitle");
    }

    /**
     * Translates a resource similar to "Cancel". The applet config dialog cancel button label..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigCancelButtonLabel" label.
     */
    public appletConfigCancelButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigCancelButtonLabel");
    }

    /**
     * Translates a resource similar to "Closes the applet configuration dialog without saving". The applet config dialog cancel button title..
     * @returns The localized resource corresponding to the "DynamicsApp_AppletConfigCancelButtonTitle" label.
     */
    public appletConfigCancelButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_AppletConfigCancelButtonTitle");
    }

    /**
     * Translates a resource similar to "{0}\n\nDetails:\n{1}". The label used when displaying a notification with details..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_WithDetailsMessageFormat" label.
     */
    public notification_WithDetailsMessageFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_Notification_WithDetailsMessageFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "{0}\n\nApp correlation ID:\n{1}". The label used when displaying a notification with a correlation ID..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_WithCorrelationIdMessageFormat" label.
     */
    public notification_WithCorrelationIdMessageFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_Notification_WithCorrelationIdMessageFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "{0}\n\n{1}". The label used when displaying a notification in two parts with a title and message..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_TitleAndMessageFormat" label.
     */
    public notification_TitleAndMessageFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_Notification_TitleAndMessageFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "{0} ({1}s)". The label used when displaying an operation duration..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_DurationFormat" label.
     */
    public notification_DurationFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_Notification_DurationFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "Error encountered". The label used as the message box label when an error is shown..
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_ErrorMessageLabel" label.
     */
    public notification_ErrorMessageLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Notification_ErrorMessageLabel");
    }

    /**
     * Translates a resource similar to "Confirmation required". The label used as the message box label when a confirmation is shown..
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_ConfirmationMessageLabel" label.
     */
    public notification_ConfirmationMessageLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Notification_ConfirmationMessageLabel");
    }

    /**
     * Translates a resource similar to "Notification encountered". The label used as the message box label when a notification is shown..
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_NotificationMessageLabel" label.
     */
    public notification_NotificationMessageLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Notification_NotificationMessageLabel");
    }

    /**
     * Translates a resource similar to "Copy details to clipboard". The label used as the message box copy button label..
     * @returns The localized resource corresponding to the "DynamicsApp_Notification_CopyButtonTitle" label.
     */
    public notification_CopyButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_Notification_CopyButtonTitle");
    }

    /**
     * Translates a resource similar to "Open message center". The label used for the notifications button tooltip..
     * @returns The localized resource corresponding to the "DynamicsApp_NotificationsButtonTooltip" label.
     */
    public notificationsButtonTooltip(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NotificationsButtonTooltip");
    }

    /**
     * Translates a resource similar to "OK". The label used for the OK button on any dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_OkButtonLabel" label.
     */
    public okButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_OkButtonLabel");
    }

    /**
     * Translates a resource similar to "Cancel". The label used for the Cancel button on any dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_CancelButtonLabel" label.
     */
    public cancelButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_CancelButtonLabel");
    }

    /**
     * Translates a resource similar to "Yes". The label used for the Yes button on any dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_YesButtonLabel" label.
     */
    public yesButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_YesButtonLabel");
    }

    /**
     * Translates a resource similar to "No". The label used for the No button on any dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_NoButtonLabel" label.
     */
    public noButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NoButtonLabel");
    }

    /**
     * Translates a resource similar to "Messages". The label used for message center dialog..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterHeading" label.
     */
    public messageCenterHeading(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterHeading");
    }

    /**
     * Translates a resource similar to "There are no messages". The label used for message center 'no messages' message..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterNoMessages" label.
     */
    public messageCenterNoMessages(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterNoMessages");
    }

    /**
     * Translates a resource similar to "Errors only". The label used for the 'errors only' checkbox..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterErrorsOnlyLabel" label.
     */
    public messageCenterErrorsOnlyLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterErrorsOnlyLabel");
    }

    /**
     * Translates a resource similar to "Clear". The label used for message center clear button..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterClearButtonLabel" label.
     */
    public messageCenterClearButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterClearButtonLabel");
    }

    /**
     * Translates a resource similar to "Clears all messages". The title used for message center clear button..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterClearButtonTitle" label.
     */
    public messageCenterClearButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterClearButtonTitle");
    }

    /**
     * Translates a resource similar to "Messages cleared". The status message shown when message center messages are cleared..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterMessagesClearedMessage" label.
     */
    public messageCenterMessagesClearedMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterMessagesClearedMessage");
    }

    /**
     * Translates a resource similar to "Done". The label used for message center done button..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterDoneButtonLabel" label.
     */
    public messageCenterDoneButtonLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterDoneButtonLabel");
    }

    /**
     * Translates a resource similar to "Closes the message center". The title used for message center done button..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterDoneButtonTitle" label.
     */
    public messageCenterDoneButtonTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterDoneButtonTitle");
    }

    /**
     * Translates a resource similar to "Learn more". The label is used in the message center as the link text to troubleshoot / get support / see help docs..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterLearnMoreLabel" label.
     */
    public messageCenterLearnMoreLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterLearnMoreLabel");
    }

    /**
     * Translates a resource similar to "Message log". The label used for message center message container..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterMessageContainerLabel" label.
     */
    public messageCenterMessageContainerLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterMessageContainerLabel");
    }

    /**
     * Translates a resource similar to "Informational notification". The label used for message center informational notification type labels..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterInformationalNotificationLabel" label.
     */
    public messageCenterInformationalNotificationLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterInformationalNotificationLabel");
    }

    /**
     * Translates a resource similar to "Error notification". The label used for message center error notification type labels..
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterErrorNotificationLabel" label.
     */
    public messageCenterErrorNotificationLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_MessageCenterErrorNotificationLabel");
    }

    /**
     * Translates a resource similar to ", has action with label "{0}", press Enter or Space key to perform the action". The label used for message center notification labels that contain a corresponding action where {0} is the notification action's label..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterNotificationHasLinkPortionLabelFormat" label.
     */
    public messageCenterNotificationHasLinkPortionLabelFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_MessageCenterNotificationHasLinkPortionLabelFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to " occurred at {0} on {1}". The label used for message center notification labels that contain date / time information where {0} is the time and {1} is the date..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterNotificationTimePortionLabelFormat" label.
     */
    public messageCenterNotificationTimePortionLabelFormat(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_MessageCenterNotificationTimePortionLabelFormat"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "{0} message "{1}"{2}{3}". The label format used for message center notification labels where {0} is the notification type message, {1} is the notification message, {2} is the optional notification time information, and {3} is the optional notification action information..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @param formatParam3 Format parameter number 3 for the label.
     * @param formatParam4 Format parameter number 4 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterNotificationLabelFormat" label.
     */
    public messageCenterNotificationLabelFormat(formatParam1: any, formatParam2: any, formatParam3: any, formatParam4: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_MessageCenterNotificationLabelFormat"), formatParam1, formatParam2, formatParam3, formatParam4);
    }

    /**
     * Translates a resource similar to "There are {0} unread error notifications in message center". The status format used when a new error notification is added where {0} is the unread error notification count..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_MessageCenterNotificationStatusFormat" label.
     */
    public messageCenterNotificationStatusFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_MessageCenterNotificationStatusFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Unknown Message Encountered". The unknown message error label..
     * @returns The localized resource corresponding to the "DynamicsApp_UnknownMessageError" label.
     */
    public unknownMessageError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnknownMessageError");
    }

    /**
     * Translates a resource similar to "This add-in enables viewing and editing of your data.\n\nA Common Data Service environment and corresponding user account are required.". The no server message label..
     * @returns The localized resource corresponding to the "DynamicsApp_NoServerMessage" label.
     */
    public noServerMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NoServerMessage");
    }

    /**
     * Translates a resource similar to "Add connection". The no server link label..
     * @returns The localized resource corresponding to the "DynamicsApp_NoServerLinkLabel" label.
     */
    public noServerLinkLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NoServerLinkLabel");
    }

    /**
     * Translates a resource similar to "Opens options dialog for connection entry". The no server link title..
     * @returns The localized resource corresponding to the "DynamicsApp_NoServerLinkTitle" label.
     */
    public noServerLinkTitle(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NoServerLinkTitle");
    }

    /**
     * Translates a resource similar to "Load applets". The no applet link label..
     * @returns The localized resource corresponding to the "DynamicsApp_NoAppletLinkLabel" label.
     */
    public noAppletLinkLabel(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_NoAppletLinkLabel");
    }

    /**
     * Translates a resource similar to "Retrieving registrations". The registrations retrieval label..
     * @returns The localized resource corresponding to the "DynamicsApp_RegistrationsRetrievalMessage" label.
     */
    public registrationsRetrievalMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RegistrationsRetrievalMessage");
    }

    /**
     * Translates a resource similar to "Registrations retrieved". The registrations retrieval succeeded label..
     * @returns The localized resource corresponding to the "DynamicsApp_RegistrationsRetrievalSucceededMessage" label.
     */
    public registrationsRetrievalSucceededMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RegistrationsRetrievalSucceededMessage");
    }

    /**
     * Translates a resource similar to "Retrieving registrations failed". The registrations retrieval failed label..
     * @returns The localized resource corresponding to the "DynamicsApp_RegistrationsRetrievalFailedMessage" label.
     */
    public registrationsRetrievalFailedMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RegistrationsRetrievalFailedMessage");
    }

    /**
     * Translates a resource similar to "Invalid URL entered. A valid URL using HTTPS must be entered.". The invalid host URL entered error label..
     * @returns The localized resource corresponding to the "DynamicsApp_InvalidHostUrlEnteredErrorMessage" label.
     */
    public invalidHostUrlEnteredErrorMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_InvalidHostUrlEnteredErrorMessage");
    }

    /**
     * Translates a resource similar to "An unexpected app error has occurred.". The unhandled error occurred message label..
     * @returns The localized resource corresponding to the "DynamicsApp_UnhandledErrorOccurredMessage" label.
     */
    public unhandledErrorOccurredMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_UnhandledErrorOccurredMessage");
    }

    /**
     * Translates a resource similar to "Retrieving server configuration". The configuration retrieval label..
     * @returns The localized resource corresponding to the "DynamicsApp_RetrievingConfigurationMessage" label.
     */
    public retrievingConfigurationMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RetrievingConfigurationMessage");
    }

    /**
     * Translates a resource similar to "Retrieving server configuration failed". The configuration retrieval failed label..
     * @returns The localized resource corresponding to the "DynamicsApp_RetrievingConfigurationFailedMessage" label.
     */
    public retrievingConfigurationFailedMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RetrievingConfigurationFailedMessage");
    }

    /**
     * Translates a resource similar to "Retrieving enhanced server configuration failed". The enhanced configuration retrieval failed label..
     * @returns The localized resource corresponding to the "DynamicsApp_RetrievingEnhancedConfigurationFailedMessage" label.
     */
    public retrievingEnhancedConfigurationFailedMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RetrievingEnhancedConfigurationFailedMessage");
    }

    /**
     * Translates a resource similar to "Server configuration retrieved". The configuration retrieval succeeded label..
     * @returns The localized resource corresponding to the "DynamicsApp_RetrievingConfigurationSucceededMessage" label.
     */
    public retrievingConfigurationSucceededMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_RetrievingConfigurationSucceededMessage");
    }

    /**
     * Translates a resource similar to "Failed to retrieve the server config". The label used when an error was encountered while retrieving the server config..
     * @returns The localized resource corresponding to the "DynamicsApp_ConfigRetrievalErrorMessage" label.
     */
    public configRetrievalErrorMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_ConfigRetrievalErrorMessage");
    }

    /**
     * Translates a resource similar to "Failed to retrieve the enhanced server config". The label used when an error was encountered while retrieving the enhanced server config..
     * @returns The localized resource corresponding to the "DynamicsApp_EnhancedConfigRetrievalErrorMessage" label.
     */
    public enhancedConfigRetrievalErrorMessage(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_EnhancedConfigRetrievalErrorMessage");
    }

    /**
     * Translates a resource similar to "Service call failed to retrieve server config. Failed status: "{0}". Thrown error: "{1}".". The notification added for the service config flow when server config fails..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_ServiceFailedToRetrieveConfigNotification" label.
     */
    public serviceFailedToRetrieveConfigNotification(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_ServiceFailedToRetrieveConfigNotification"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "Resource failed to retrieve server config. Failure reason: "{0}".". The notification added for the resource config flow when server config fails..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_ResourceFailedToRetrieveConfigNotification" label.
     */
    public resourceFailedToRetrieveConfigNotification(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_ResourceFailedToRetrieveConfigNotification"), formatParam1);
    }

    /**
     * Translates a resource similar to "Failed to load". The error added when a resource fails to load..
     * @returns The localized resource corresponding to the "DynamicsApp_ResourceFailedToLoadError" label.
     */
    public resourceFailedToLoadError(): string {
        return this.labelResolver.resolveLabel("DynamicsApp_ResourceFailedToLoadError");
    }

    /**
     * Translates a resource similar to "Available features for current Office version\n- Authentication dialog for ADFS: {0}\n- Hidden workbook cache: {1}\n- Publishing filtered tables: {2}". The label used to list the features currently available within the add-in..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @param formatParam3 Format parameter number 3 for the label.
     * @returns The localized resource corresponding to the "DynamicsApp_FeaturesNotificationFormat" label.
     */
    public featuresNotificationFormat(formatParam1: any, formatParam2: any, formatParam3: any): string {
        return $format(this.labelResolver.resolveLabel("DynamicsApp_FeaturesNotificationFormat"), formatParam1, formatParam2, formatParam3);
    }
}

/** The mechanism used to resolve labels related to the "Message" concept. */
export class MessageLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Close this window to resume". Message displayed on authenticated page to indicate to the user they should close the window..
     * @returns The localized resource corresponding to the "Message_CloseWindow" label.
     */
    public closeWindow(): string {
        return this.labelResolver.resolveLabel("Message_CloseWindow");
    }
}

/** The mechanism used to resolve labels related to the "AppletMessaging" concept. */
export class AppletMessagingLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "The request timed out waiting for a response.". The request timed out error message..
     * @returns The localized resource corresponding to the "AppletMessaging_RequestTimedOutMessage" label.
     */
    public requestTimedOutMessage(): string {
        return this.labelResolver.resolveLabel("AppletMessaging_RequestTimedOutMessage");
    }
}

/** The mechanism used to resolve labels related to the "Error" concept. */
export class ErrorLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Unknown message type: {0}". The error message returned when an unknown message type is encountered..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Error_UnknownMessageType" label.
     */
    public unknownMessageType(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Error_UnknownMessageType"), formatParam1);
    }
}

/** The mechanism used to resolve labels related to the "Common" concept. */
export class CommonLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "OK". The OK button label..
     * @returns The localized resource corresponding to the "Common_OkButtonLabel" label.
     */
    public okButtonLabel(): string {
        return this.labelResolver.resolveLabel("Common_OkButtonLabel");
    }

    /**
     * Translates a resource similar to "True". The string used for a true value..
     * @returns The localized resource corresponding to the "Common_TrueString" label.
     */
    public trueString(): string {
        return this.labelResolver.resolveLabel("Common_TrueString");
    }

    /**
     * Translates a resource similar to "False". The string used for a false value..
     * @returns The localized resource corresponding to the "Common_FalseString" label.
     */
    public falseString(): string {
        return this.labelResolver.resolveLabel("Common_FalseString");
    }

    /**
     * Translates a resource similar to "Retry". The label used on controls enabling a retry of an action..
     * @returns The localized resource corresponding to the "Common_Retry" label.
     */
    public retry(): string {
        return this.labelResolver.resolveLabel("Common_Retry");
    }
}

/** The mechanism used to resolve labels related to the "Addin" concept. */
export class AddinLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "The Microsoft Common Data Service Office Add-in enables data connections to Microsoft Common Data Service systems.". The description of the add-in, used within the add-in's manifest..
     * @returns The localized resource corresponding to the "Addin_Description" label.
     */
    public description(): string {
        return this.labelResolver.resolveLabel("Addin_Description");
    }
}

/** The mechanism used to resolve labels related to the "Discovery" concept. */
export class DiscoveryLabelResolver {

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(private labelResolver: LabelResolver) { }

    /**
     * Translates a resource similar to "Retrieving environments". The status displayed while environments are being retrieved..
     * @returns The localized resource corresponding to the "Discovery_RetrievingEnvironments" label.
     */
    public retrievingEnvironments(): string {
        return this.labelResolver.resolveLabel("Discovery_RetrievingEnvironments");
    }

    /**
     * Translates a resource similar to "Environments retrieved". The status displayed once environments are retrieved..
     * @returns The localized resource corresponding to the "Discovery_EnvironmentsRetrieved" label.
     */
    public environmentsRetrieved(): string {
        return this.labelResolver.resolveLabel("Discovery_EnvironmentsRetrieved");
    }

    /**
     * Translates a resource similar to "Environment retrieval failed". The status displayed when retrieving environments fails..
     * @returns The localized resource corresponding to the "Discovery_RetrievingEnvironmentsFailed" label.
     */
    public retrievingEnvironmentsFailed(): string {
        return this.labelResolver.resolveLabel("Discovery_RetrievingEnvironmentsFailed");
    }

    /**
     * Translates a resource similar to "Environment not found. Please ensure that user {0} ({1}) has appropriate access to the environment with ID "{2}".". The error shown when an environment cannot be found. Parameter {0} represents the user's display name while {1} represents the user name. Parameter {2} is a GUID that represents an environment's ID..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @param formatParam3 Format parameter number 3 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentNotFoundErrorFormat" label.
     */
    public environmentNotFoundErrorFormat(formatParam1: any, formatParam2: any, formatParam3: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentNotFoundErrorFormat"), formatParam1, formatParam2, formatParam3);
    }

    /**
     * Translates a resource similar to "Call to retrieve environments available for user {0} ({1}) has failed.". The error shown when a call to the discovery service fails. Parameter {0} represents the user's display name while {1} represents the user name..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "Discovery_ServiceCallFailed" label.
     */
    public serviceCallFailed(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_ServiceCallFailed"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "CDS Endpoint". The label for the CDS endpoint select..
     * @returns The localized resource corresponding to the "Discovery_EndpointSelectLabel" label.
     */
    public endpointSelectLabel(): string {
        return this.labelResolver.resolveLabel("Discovery_EndpointSelectLabel");
    }

    /**
     * Translates a resource similar to "Production". The label for the production endpoint option..
     * @returns The localized resource corresponding to the "Discovery_ProductionEndpointOption" label.
     */
    public productionEndpointOption(): string {
        return this.labelResolver.resolveLabel("Discovery_ProductionEndpointOption");
    }

    /**
     * Translates a resource similar to "TIP1". The label for the Test In Production (1) endpoint option..
     * @returns The localized resource corresponding to the "Discovery_Tip1EndpointOption" label.
     */
    public tip1EndpointOption(): string {
        return this.labelResolver.resolveLabel("Discovery_Tip1EndpointOption");
    }

    /**
     * Translates a resource similar to "Direct Connect". The label for the direct connection option..
     * @returns The localized resource corresponding to the "Discovery_DirectConnectOption" label.
     */
    public directConnectOption(): string {
        return this.labelResolver.resolveLabel("Discovery_DirectConnectOption");
    }

    /**
     * Translates a resource similar to "Open discovery experience". The tooltip applied to the discovery button when the discovery experience can be opened..
     * @returns The localized resource corresponding to the "Discovery_OpenExperienceTooltip" label.
     */
    public openExperienceTooltip(): string {
        return this.labelResolver.resolveLabel("Discovery_OpenExperienceTooltip");
    }

    /**
     * Translates a resource similar to "You must authenticate prior to using the discovery experience". The tooltip applied to the discovery button when the user isn't authenticated..
     * @returns The localized resource corresponding to the "Discovery_NotAuthenticatedTooltip" label.
     */
    public notAuthenticatedTooltip(): string {
        return this.labelResolver.resolveLabel("Discovery_NotAuthenticatedTooltip");
    }

    /**
     * Translates a resource similar to "Changing to use this Common Data Service endpoint requires the add-in to restart.\n\nContinue?". The confirmation message displayed when a user changes an endpoint option that requies the add-in to restart..
     * @returns The localized resource corresponding to the "Discovery_EndpointChangeRequiresRestart" label.
     */
    public endpointChangeRequiresRestart(): string {
        return this.labelResolver.resolveLabel("Discovery_EndpointChangeRequiresRestart");
    }

    /**
     * Translates a resource similar to "You have made a selection that requires the app to be reloaded.\n\nContinue?". The confirmation message displayed when a user selects a new environment, which requies the add-in to restart..
     * @returns The localized resource corresponding to the "Discovery_DiscoverySelectionRequiresRestart" label.
     */
    public discoverySelectionRequiresRestart(): string {
        return this.labelResolver.resolveLabel("Discovery_DiscoverySelectionRequiresRestart");
    }

    /**
     * Translates a resource similar to "Discovery list". The label applied to the discovery listbox..
     * @returns The localized resource corresponding to the "Discovery_ListBoxLabel" label.
     */
    public listBoxLabel(): string {
        return this.labelResolver.resolveLabel("Discovery_ListBoxLabel");
    }

    /**
     * Translates a resource similar to "Environment {0}, owned by {1}". The label applied to the discovery listbox list items..
     * @param formatParam1 Format parameter number 1 for the label.
     * @param formatParam2 Format parameter number 2 for the label.
     * @returns The localized resource corresponding to the "Discovery_ListItemLabel" label.
     */
    public listItemLabel(formatParam1: any, formatParam2: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_ListItemLabel"), formatParam1, formatParam2);
    }

    /**
     * Translates a resource similar to "No environments available". The label displayed when no environments are available..
     * @returns The localized resource corresponding to the "Discovery_ListBoxNoEnvironments" label.
     */
    public listBoxNoEnvironments(): string {
        return this.labelResolver.resolveLabel("Discovery_ListBoxNoEnvironments");
    }

    /**
     * Translates a resource similar to "Name". The heading of the discovery experience name column..
     * @returns The localized resource corresponding to the "Discovery_NameColumnHeading" label.
     */
    public nameColumnHeading(): string {
        return this.labelResolver.resolveLabel("Discovery_NameColumnHeading");
    }

    /**
     * Translates a resource similar to "Owner". The heading of the discovery experience owner column..
     * @returns The localized resource corresponding to the "Discovery_OwnerColumnHeading" label.
     */
    public ownerColumnHeading(): string {
        return this.labelResolver.resolveLabel("Discovery_OwnerColumnHeading");
    }

    /**
     * Translates a resource similar to "Environment Details". The heading of the details section..
     * @returns The localized resource corresponding to the "Discovery_DetailsSectionHeading" label.
     */
    public detailsSectionHeading(): string {
        return this.labelResolver.resolveLabel("Discovery_DetailsSectionHeading");
    }

    /**
     * Translates a resource similar to "Environment name: {0}". The format used to display an environment's name in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentNameFormat" label.
     */
    public environmentNameFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentNameFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Version: {0}". The format used to display an environment's version in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentVersionFormat" label.
     */
    public environmentVersionFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentVersionFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Owner name: {0}". The format used to display an environment's owner's name in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentOwnerNameFormat" label.
     */
    public environmentOwnerNameFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentOwnerNameFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Owner email: {0}". The format used to display an environment's owner's email in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentOwnerEmailFormat" label.
     */
    public environmentOwnerEmailFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentOwnerEmailFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Created by name: {0}". The format used to display an environment's creator's name in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentCreatorNameFormat" label.
     */
    public environmentCreatorNameFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentCreatorNameFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Created by email: {0}". The format used to display an environment's creator's email in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentCreatorEmailFormat" label.
     */
    public environmentCreatorEmailFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentCreatorEmailFormat"), formatParam1);
    }

    /**
     * Translates a resource similar to "Created: {0}". The format used to display an environment's creation date in the details section..
     * @param formatParam1 Format parameter number 1 for the label.
     * @returns The localized resource corresponding to the "Discovery_EnvironmentCreationDateFormat" label.
     */
    public environmentCreationDateFormat(formatParam1: any): string {
        return $format(this.labelResolver.resolveLabel("Discovery_EnvironmentCreationDateFormat"), formatParam1);
    }
}

/** The mechanism used to resolve labels. */
export class GeneratedLabelResolver {

    /** The labels related to the "AppletInteraction" concept. */
    public readonly appletInteraction: AppletInteractionLabelResolver;

    /** The labels related to the "OfficeInteraction" concept. */
    public readonly officeInteraction: OfficeInteractionLabelResolver;

    /** The labels related to the "Authentication" concept. */
    public readonly authentication: AuthenticationLabelResolver;

    /** The labels related to the "DynamicsApp" concept. */
    public readonly dynamicsApp: DynamicsAppLabelResolver;

    /** The labels related to the "Message" concept. */
    public readonly message: MessageLabelResolver;

    /** The labels related to the "AppletMessaging" concept. */
    public readonly appletMessaging: AppletMessagingLabelResolver;

    /** The labels related to the "Error" concept. */
    public readonly error: ErrorLabelResolver;

    /** The labels related to the "Common" concept. */
    public readonly common: CommonLabelResolver;

    /** The labels related to the "Addin" concept. */
    public readonly addin: AddinLabelResolver;

    /** The labels related to the "Discovery" concept. */
    public readonly discovery: DiscoveryLabelResolver;

    /**
     * @param labelResolver The label loader used to resolve labels.
     */
    constructor(public readonly labelResolver: LabelResolver) {
        this.appletInteraction = new AppletInteractionLabelResolver(labelResolver);
        this.officeInteraction = new OfficeInteractionLabelResolver(labelResolver);
        this.authentication = new AuthenticationLabelResolver(labelResolver);
        this.dynamicsApp = new DynamicsAppLabelResolver(labelResolver);
        this.message = new MessageLabelResolver(labelResolver);
        this.appletMessaging = new AppletMessagingLabelResolver(labelResolver);
        this.error = new ErrorLabelResolver(labelResolver);
        this.common = new CommonLabelResolver(labelResolver);
        this.addin = new AddinLabelResolver(labelResolver);
        this.discovery = new DiscoveryLabelResolver(labelResolver);
    }
}
// tslint:enable:max-line-length
